import { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { useApiToken } from '@/auth/hooks';

interface CurationItem {
  workspaceId: string;
  workspaceName: string;
  total: number;
  curated: number;
}

const CURATED_COLOR = 'hsl(152, 69%, 36%)';
const UNCURATED_COLOR = 'hsl(214, 16%, 85%)';

export function CurationDonutCharts() {
  const { getAccessToken } = useApiToken();
  const [items, setItems] = useState<CurationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const token = await getAccessToken();
        const headers: HeadersInit = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        const res = await fetch('/api/dotnet/audit-logs/curation', { headers });
        if (res.ok) setItems(await res.json());
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground">Curation-Fortschritt pro Tool</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border-border">
              <CardContent className="flex flex-col items-center p-3">
                <div className="h-20 w-20 animate-pulse rounded-full bg-muted" />
                <div className="mt-2 h-3 w-16 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground">Curation-Fortschritt pro Tool</h3>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Keine Dokumente vorhanden.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-foreground">Curation-Fortschritt pro Tool</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((item) => {
          const remaining = Math.max(0, item.total - item.curated);
          const pct = item.total > 0 ? Math.round((100 * item.curated) / item.total) : 0;
          const data = [
            { name: 'Kuratiert', value: item.curated },
            { name: 'Offen', value: remaining },
          ];
          const pctColor = pct >= 80
            ? 'text-[hsl(var(--status-ok))]'
            : pct >= 50
              ? 'text-[hsl(var(--status-warning))]'
              : 'text-[hsl(var(--status-error))]';
          return (
            <Card key={item.workspaceId} className="border-border">
              <CardContent className="flex flex-col items-center p-3">
                <div className="relative h-20 w-20">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        innerRadius={26}
                        outerRadius={36}
                        dataKey="value"
                        strokeWidth={0}
                        startAngle={90}
                        endAngle={-270}
                      >
                        <Cell fill={CURATED_COLOR} />
                        <Cell fill={UNCURATED_COLOR} />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-sm font-bold ${pctColor}`}>{pct}%</span>
                  </div>
                </div>
                <p className="mt-1.5 text-xs font-medium text-foreground">{item.workspaceName}</p>
                <p className="text-[10px] text-muted-foreground">{item.curated}/{item.total}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
